import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { HiArrowsPointingIn, HiArrowsPointingOut, HiMiniPause, HiMiniPlay, HiSpeakerWave, HiSpeakerXMark } from 'react-icons/hi2'
import type { OverviewVideo, OverviewVideoFileSource } from '@/api/endpoints'

export type VideoPlaybackState = {
  currentTime: number
  isMuted: boolean
  isPlaying: boolean
  qualityMode: string | null
}

type ProductOverviewVideoPlayerProps = {
  video: OverviewVideo
  title: string
  isModal?: boolean
  active?: boolean
  initialPlaybackState?: VideoPlaybackState | null
  onOpenModal?: (state: VideoPlaybackState) => void
  onCloseModal?: () => void
  onPlaybackStateChange?: (state: VideoPlaybackState) => void
}

const formatTime = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0:00'
  }

  const totalSeconds = Math.floor(value)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const sortSources = (sources: OverviewVideoFileSource[]): OverviewVideoFileSource[] => {
  const sorted = [...sources].sort((left, right) => (right.height ?? 0) - (left.height ?? 0))
  const seenUrls = new Set<string>()
  const seenLabels = new Set<string>()

  return sorted.filter((source) => {
    const url = source.url.trim()
    const label = source.label.trim().toLowerCase()

    if (seenUrls.has(url)) {
      return false
    }

    if (label && seenLabels.has(label)) {
      return false
    }

    seenUrls.add(url)
    if (label) {
      seenLabels.add(label)
    }

    return true
  })
}

const SEEK_STEP_SECONDS = 5
const SEEK_SETTLE_EPSILON = 0.75
const CONTROL_HIDE_DELAY_MS = 2200

type ScreenOrientationController = ScreenOrientation & {
  lock?: (orientation: 'any' | 'natural' | 'landscape' | 'portrait' | 'portrait-primary' | 'portrait-secondary' | 'landscape-primary' | 'landscape-secondary') => Promise<void>
  unlock?: () => void
}

export default function ProductOverviewVideoPlayer({
  video,
  title,
  isModal = false,
  active = true,
  initialPlaybackState,
  onOpenModal,
  onCloseModal,
  onPlaybackStateChange,
}: ProductOverviewVideoPlayerProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const sources = useMemo(() => sortSources(video.sources), [video.sources])
  const primarySourceUrl = sources[0]?.url ?? null
  const [qualityMode, setQualityMode] = useState<string | null>(primarySourceUrl)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isBuffering, setIsBuffering] = useState(true)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [bufferedTime, setBufferedTime] = useState(0)
  const [pendingSeekTime, setPendingSeekTime] = useState<number | null>(null)
  const [scrubTime, setScrubTime] = useState<number | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const resumeTimeRef = useRef(0)
  const resumePlaybackRef = useRef(false)
  const lastAppliedPlaybackRef = useRef<string | null>(null)
  const isUserSeekingRef = useRef(false)
  const isScrubbingRef = useRef(false)
  const controlsHideTimerRef = useRef<number | null>(null)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [isPointerInside, setIsPointerInside] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const isImmersiveMobileModal = isTouchDevice && isModal
  const keepControlsVisible = isTouchDevice && (isFullscreen || isModal)
  const canCloseModalFromControls = isImmersiveMobileModal && typeof onCloseModal === 'function'

  useEffect(() => {
    setQualityMode(primarySourceUrl)
    setIsPlaying(false)
    setIsBuffering(true)
    setDuration(0)
    setCurrentTime(0)
    setBufferedTime(0)
    setPendingSeekTime(null)
    setScrubTime(null)
    setControlsVisible(true)
    setIsFullscreen(false)
  }, [primarySourceUrl, video.url, video.embed_url, video.preview_image])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }

    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)')
    const syncTouchCapability = () => setIsTouchDevice(mediaQuery.matches)

    syncTouchCapability()

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncTouchCapability)
      return () => mediaQuery.removeEventListener('change', syncTouchCapability)
    }

    mediaQuery.addListener(syncTouchCapability)
    return () => mediaQuery.removeListener(syncTouchCapability)
  }, [])

  const clearControlsHideTimer = useCallback(() => {
    if (controlsHideTimerRef.current !== null) {
      window.clearTimeout(controlsHideTimerRef.current)
      controlsHideTimerRef.current = null
    }
  }, [])

  const showControls = useCallback(() => {
    clearControlsHideTimer()
    setControlsVisible(true)
  }, [clearControlsHideTimer])

  const scheduleControlsHide = useCallback(() => {
    clearControlsHideTimer()

    if (
      keepControlsVisible
      || !active
      || !isPlaying
      || isBuffering
      || isPointerInside
      || isUserSeekingRef.current
      || isScrubbingRef.current
    ) {
      return
    }

    controlsHideTimerRef.current = window.setTimeout(() => {
      if (!isPointerInside && !isUserSeekingRef.current && !isScrubbingRef.current) {
        setControlsVisible(false)
      }
    }, CONTROL_HIDE_DELAY_MS)
  }, [active, clearControlsHideTimer, isBuffering, isPlaying, isPointerInside, keepControlsVisible])

  const lockLandscapeOrientation = useCallback(async () => {
    if (typeof screen === 'undefined') {
      return
    }

    const orientation = screen.orientation as ScreenOrientationController | undefined
    if (typeof orientation?.lock !== 'function') {
      return
    }

    try {
      await orientation.lock('landscape')
    } catch {
      // Some mobile browsers disallow orientation lock outside specific fullscreen flows.
    }
  }, [])

  const unlockOrientation = useCallback(() => {
    if (typeof screen === 'undefined') {
      return
    }

    const orientation = screen.orientation as ScreenOrientationController | undefined
    if (typeof orientation?.unlock !== 'function') {
      return
    }

    try {
      orientation.unlock()
    } catch {
      // Ignore unsupported unlock failures.
    }
  }, [])

  const selectedSource = useMemo(() => {
    if (!qualityMode) {
      return sources[0] ?? null
    }

    return sources.find((source) => source.url === qualityMode) ?? sources[0] ?? null
  }, [qualityMode, sources])

  const readPlaybackState = useCallback((): VideoPlaybackState => {
    const element = videoRef.current

    return {
      currentTime: element?.currentTime ?? currentTime,
      isMuted: element?.muted ?? isMuted,
      isPlaying: element ? !element.paused && !element.ended : isPlaying,
      qualityMode: qualityMode ?? primarySourceUrl,
    }
  }, [currentTime, isMuted, isPlaying, primarySourceUrl, qualityMode])

  useEffect(() => {
    onPlaybackStateChange?.(readPlaybackState())
  }, [currentTime, isMuted, isPlaying, qualityMode, onPlaybackStateChange, readPlaybackState])
  const displayedCurrentTime = scrubTime ?? pendingSeekTime ?? currentTime

  useEffect(() => {
    if (!active) {
      clearControlsHideTimer()
      return
    }

    if (keepControlsVisible || !isPlaying || isBuffering || isPointerInside) {
      showControls()
      return
    }

    scheduleControlsHide()

    return clearControlsHideTimer
  }, [
    active,
    clearControlsHideTimer,
    keepControlsVisible,
    isBuffering,
    isPlaying,
    isPointerInside,
    scheduleControlsHide,
    showControls,
  ])

  useEffect(() => () => {
    clearControlsHideTimer()
    unlockOrientation()
  }, [clearControlsHideTimer, unlockOrientation])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const handleFullscreenChange = () => {
      const wrapper = wrapperRef.current
      const fullscreenNow = Boolean(wrapper && document.fullscreenElement === wrapper)
      setIsFullscreen(fullscreenNow)
      showControls()

      if (fullscreenNow) {
        void lockLandscapeOrientation()
        return
      }

      unlockOrientation()
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [lockLandscapeOrientation, showControls, unlockOrientation])

  const syncBufferedTime = useCallback((element: HTMLVideoElement) => {
    if (element.buffered.length === 0) {
      setBufferedTime(0)
      return
    }

    const bufferedEnd = element.buffered.end(element.buffered.length - 1)
    setBufferedTime(bufferedEnd)
  }, [])

  useEffect(() => {
    const element = videoRef.current
    if (!element || !selectedSource?.url) {
      return
    }

    resumeTimeRef.current = element.currentTime || 0
    resumePlaybackRef.current = !element.paused && !element.ended
    setIsBuffering(true)

    if (element.src !== selectedSource.url) {
      element.src = selectedSource.url
      element.load()
    }
  }, [selectedSource?.url])

  useEffect(() => {
    if (active) {
      return
    }

    const element = videoRef.current
    if (!element || element.paused) {
      return
    }

    element.pause()
  }, [active])

  useEffect(() => {
    if (!initialPlaybackState) {
      return
    }

    const playbackKey = JSON.stringify(initialPlaybackState)
    if (lastAppliedPlaybackRef.current === playbackKey) {
      return
    }

    lastAppliedPlaybackRef.current = playbackKey
    setQualityMode(
      initialPlaybackState.qualityMode && sources.some((source) => source.url === initialPlaybackState.qualityMode)
        ? initialPlaybackState.qualityMode
        : primarySourceUrl,
    )
    setIsMuted(initialPlaybackState.isMuted)
    setCurrentTime(initialPlaybackState.currentTime)
    setIsPlaying(initialPlaybackState.isPlaying)
    setPendingSeekTime(null)
    resumeTimeRef.current = initialPlaybackState.currentTime
    resumePlaybackRef.current = initialPlaybackState.isPlaying

    const element = videoRef.current
    if (element) {
      element.muted = initialPlaybackState.isMuted
      element.currentTime = Math.max(0, initialPlaybackState.currentTime)
      if (initialPlaybackState.isPlaying && element.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        void element.play().catch(() => undefined)
      }
    }
  }, [initialPlaybackState, primarySourceUrl, sources])

  const togglePlayback = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    showControls()

    if (element.paused) {
      void element.play().catch(() => undefined)
      return
    }

    element.pause()
  }, [])

  const handleSeek = useCallback((nextTime: number) => {
    const element = videoRef.current
    if (!element) {
      return
    }

    showControls()
    resumePlaybackRef.current = !element.paused && !element.ended
    isUserSeekingRef.current = true
    setPendingSeekTime(nextTime)
    setIsBuffering(true)
    element.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [])

  const toggleMute = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    showControls()
    element.muted = !element.muted
    setIsMuted(element.muted)
  }, [showControls])

  const handleExpand = useCallback(() => {
    if (isTouchDevice) {
      if (!isModal) {
        onOpenModal?.(readPlaybackState())
      }
      return
    }

    const wrapper = wrapperRef.current
    if (wrapper && typeof wrapper.requestFullscreen === 'function') {
      showControls()
      void wrapper.requestFullscreen()
        .then(() => lockLandscapeOrientation())
        .catch(() => {
          onOpenModal?.(readPlaybackState())
        })
      return
    }

    onOpenModal?.(readPlaybackState())
  }, [isModal, isTouchDevice, lockLandscapeOrientation, onOpenModal, readPlaybackState, showControls])

  const handleToggleFullscreen = useCallback(() => {
    if (canCloseModalFromControls) {
      showControls()
      onCloseModal?.()
      return
    }

    const wrapper = wrapperRef.current
    if (!wrapper) {
      return
    }

    showControls()

    if (typeof document !== 'undefined' && document.fullscreenElement === wrapper) {
      void document.exitFullscreen().catch(() => undefined)
      return
    }

    handleExpand()
  }, [canCloseModalFromControls, handleExpand, onCloseModal, showControls])

  const handleLoadedMetadata = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    setDuration(Number.isFinite(element.duration) ? element.duration : 0)

    if (resumeTimeRef.current > 0) {
      const safeTime = Math.min(
        resumeTimeRef.current,
        Number.isFinite(element.duration) && element.duration > 0
          ? Math.max(0, element.duration - 0.25)
          : resumeTimeRef.current,
      )
      element.currentTime = safeTime
      setCurrentTime(safeTime)
      setPendingSeekTime(null)
      resumeTimeRef.current = 0
    }
  }, [])

  const handleCanPlay = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    if (pendingSeekTime !== null) {
      const isSeekSettled =
        Math.abs(element.currentTime - pendingSeekTime) <= SEEK_SETTLE_EPSILON

      if (!isSeekSettled) {
        if (element.readyState >= HTMLMediaElement.HAVE_METADATA && !element.seeking) {
          element.currentTime = pendingSeekTime
        }
        return
      }

      setCurrentTime(element.currentTime)
      setPendingSeekTime(null)
      isUserSeekingRef.current = false
    }

    setIsBuffering(false)

    if (resumePlaybackRef.current) {
      resumePlaybackRef.current = false
      void element.play().catch(() => undefined)
    }
  }, [pendingSeekTime])

  const handleWaiting = useCallback(() => {
    showControls()
    setIsBuffering(true)
  }, [showControls])

  const handleTimeUpdate = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    if (
      pendingSeekTime !== null
      && Math.abs(element.currentTime - pendingSeekTime) > SEEK_SETTLE_EPSILON
    ) {
      if (element.readyState >= HTMLMediaElement.HAVE_METADATA && !element.seeking) {
        element.currentTime = pendingSeekTime
      }
      syncBufferedTime(element)
      return
    }

    if (pendingSeekTime !== null) {
      setPendingSeekTime(null)
      isUserSeekingRef.current = false
    }

    setCurrentTime(element.currentTime)
    syncBufferedTime(element)
  }, [pendingSeekTime, syncBufferedTime])

  const handleProgress = useCallback(() => {
    const element = videoRef.current
    if (!element) {
      return
    }

    syncBufferedTime(element)
  }, [syncBufferedTime])

  const handleSeeking = useCallback(() => {
    isUserSeekingRef.current = true
    showControls()
    setIsBuffering(true)
  }, [showControls])

  const handleSeeked = useCallback(() => {
    isUserSeekingRef.current = false

    const element = videoRef.current
    if (!element) {
      return
    }

    if (pendingSeekTime !== null) {
      const isSeekSettled =
        Math.abs(element.currentTime - pendingSeekTime) <= SEEK_SETTLE_EPSILON

      if (!isSeekSettled) {
        if (element.readyState >= HTMLMediaElement.HAVE_METADATA) {
          element.currentTime = pendingSeekTime
        }
        return
      }

      setCurrentTime(element.currentTime)
      setPendingSeekTime(null)
    }

    if (element.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsBuffering(false)
    }
  }, [pendingSeekTime])

  const seekBy = useCallback((deltaSeconds: number) => {
    const element = videoRef.current
    if (!element) {
      return
    }

    const maxDuration =
      Number.isFinite(element.duration) && element.duration > 0
        ? element.duration
        : Number.MAX_SAFE_INTEGER
    const nextTime = Math.max(0, Math.min(maxDuration, element.currentTime + deltaSeconds))

    showControls()
    resumePlaybackRef.current = !element.paused && !element.ended
    isUserSeekingRef.current = true
    setPendingSeekTime(nextTime)
    setIsBuffering(true)
    element.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [showControls])

  const startScrubbing = useCallback(() => {
    isScrubbingRef.current = true
    showControls()
    setScrubTime(pendingSeekTime ?? currentTime)
  }, [currentTime, pendingSeekTime, showControls])

  const commitScrub = useCallback(() => {
    if (!isScrubbingRef.current) {
      return
    }

    isScrubbingRef.current = false
    const nextTime = scrubTime
    setScrubTime(null)
    if (nextTime === null) {
      return
    }

    handleSeek(nextTime)
  }, [handleSeek, scrubTime])

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    showControls()
    const target = event.target as HTMLElement | null
    if (target && (target.tagName === 'SELECT' || target.tagName === 'OPTION')) {
      return
    }

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      togglePlayback()
      return
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      seekBy(SEEK_STEP_SECONDS)
      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      seekBy(-SEEK_STEP_SECONDS)
    }
  }, [seekBy, showControls, togglePlayback])

  if (!selectedSource) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-[28px] bg-gray-950 text-sm text-white/80">
        Видео временно недоступно.
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={showControls}
      onBlur={scheduleControlsHide}
      className={`${isImmersiveMobileModal ? 'flex h-full w-full flex-col overflow-hidden shadow-none' : 'overflow-hidden'} bg-gray-950 outline-none focus-visible:ring-2 focus-visible:ring-primary/70 ${
        isFullscreen || isImmersiveMobileModal ? 'rounded-none' : 'rounded-[10px]'
      }`}
    >
      <div
        className={`group relative bg-black ${isImmersiveMobileModal ? 'min-h-0 flex-1' : 'aspect-video'} ${!controlsVisible && isPlaying ? 'cursor-none' : ''}`}
        onMouseEnter={() => {
          setIsPointerInside(true)
          showControls()
        }}
        onMouseLeave={() => {
          setIsPointerInside(false)
          scheduleControlsHide()
        }}
        onMouseMove={showControls}
        onTouchStart={showControls}
        onTouchMove={showControls}
      >
        <video
          ref={videoRef}
          poster={video.preview_image || undefined}
          preload="auto"
          playsInline
          className="h-full w-full bg-black object-contain"
          onClick={(event) => {
            event.currentTarget.parentElement?.parentElement?.focus()
            if (!controlsVisible && !event.currentTarget.paused) {
              showControls()
              return
            }
            togglePlayback()
          }}
          onDoubleClick={(event) => {
            event.preventDefault()
            event.currentTarget.parentElement?.parentElement?.focus()
            showControls()
            handleToggleFullscreen()
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={handleCanPlay}
          onCanPlayThrough={() => setIsBuffering(false)}
          onWaiting={handleWaiting}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
          onTimeUpdate={handleTimeUpdate}
          onProgress={handleProgress}
          onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
        />

        {!isPlaying ? (
          <button
            type="button"
            onClick={togglePlayback}
            className="absolute inset-0 flex items-center justify-center bg-black/10 transition hover:bg-black/20"
            aria-label="Воспроизвести видео"
          >
            <span className={`flex ${isFullscreen ? "h-18 w-18" : "h-10 w-10 lg:h-18 lg:w-18"} items-center justify-center rounded-full bg-white/95 text-gray-950 shadow-[0_20px_45px_rgba(15,23,42,0.35)]`}>
              <HiMiniPlay className="ml-1 h-9 w-9" />
            </span>
          </button>
        ) : null}

        {isBuffering ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-4 border-white/20 border-t-white animate-spin" />
          </div>
        ) : null}

        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-opacity duration-300 ${
            controlsVisible || !isPlaying || isBuffering ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <div
          className={`absolute inset-x-0 bottom-0 flex flex-col gap-3 px-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] transition-opacity duration-300 sm:px-4 sm:pb-[calc(env(safe-area-inset-bottom,0px)+16px)] ${
            controlsVisible || !isPlaying || isBuffering ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <div className="relative h-1.5 overflow-hidden rounded-full bg-white/20">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-white/35"
              style={{ width: `${duration > 0 ? Math.min(100, (bufferedTime / duration) * 100) : 0}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary"
              style={{ width: `${duration > 0 ? Math.min(100, (displayedCurrentTime / duration) * 100) : 0}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(displayedCurrentTime, duration || 0)}
              onPointerDown={startScrubbing}
              onPointerUp={commitScrub}
              onPointerCancel={commitScrub}
              onBlur={commitScrub}
              onChange={(event) => {
                const nextValue = Number(event.target.value)
                if (isScrubbingRef.current) {
                  setScrubTime(nextValue)
                  return
                }

                handleSeek(nextValue)
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label="Позиция воспроизведения"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-white">
            <button
              type="button"
              onClick={togglePlayback}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition hover:bg-white/20"
              aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
            >
              {isPlaying ? <HiMiniPause className="h-5 w-5" /> : <HiMiniPlay className="ml-0.5 h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={toggleMute}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition hover:bg-white/20"
              aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
            >
              {isMuted ? <HiSpeakerXMark className="h-5 w-5" /> : <HiSpeakerWave className="h-5 w-5" />}
            </button>

            <div className="min-w-24 text-xs font-medium text-white/80 sm:text-sm">
              {formatTime(displayedCurrentTime)} / {formatTime(duration)}
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              {sources.length > 1 ? (
                <label className="flex items-center gap-2 rounded-full bg-white/12 px-3 py-2 text-xs text-white/90 sm:text-sm">
                  <select
                    value={qualityMode ?? selectedSource.url}
                    onChange={(event) => {
                      setQualityMode(event.target.value)
                    }}
                    className="bg-transparent outline-none"
                  >
                    {sources.map((source) => (
                      <option key={source.url} value={source.url}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {selectedSource && (!isImmersiveMobileModal || canCloseModalFromControls) ? (
                <button
                  type="button"
                  onClick={handleToggleFullscreen}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition hover:bg-white/20"
                  aria-label={canCloseModalFromControls ? 'Выйти из полноэкранного режима' : isFullscreen ? 'Выйти из полного экрана' : 'Открыть видео на полный экран'}
                >
                  {canCloseModalFromControls || isFullscreen ? <HiArrowsPointingIn className="h-5 w-5" /> : <HiArrowsPointingOut className="h-5 w-5" />}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {!isImmersiveMobileModal ? (
        <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-gray-950/95 px-4 py-3 text-xs text-white/70 sm:px-5 sm:text-sm">
          <div className="min-w-0">
            <p className="truncate font-medium text-white">{title}</p>
            <p className="truncate text-white/55">
              {selectedSource.label}
            </p>
          </div>
          {video.url && video.source_type !== 'uploaded_file' ? (
            <a
              href={video.url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-full border border-white/15 px-3 py-1.5 text-white/80 transition hover:border-white/30 hover:text-white"
            >
              Источник
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
